/**
 * Hook pour gérer les anniversaires depuis l'admin
 * @module admin/hooks/useBirthdays
 */

import { useState, useCallback } from 'react';
import {
  getBirthdayStatus,
  getBirthdayHistory,
  testBirthdayAnnounce,
  checkBirthdays,
} from '../services/maintenanceService';

/**
 * Hook de gestion des anniversaires pour l'admin
 * @returns {Object} État et fonctions pour les anniversaires
 */
export const useBirthdays = () => {
  const [birthdayStatus, setBirthdayStatus] = useState(null);
  const [birthdayHistory, setBirthdayHistory] = useState([]);
  const [birthdayLoading, setBirthdayLoading] = useState(false);
  const [birthdayResult, setBirthdayResult] = useState(null);
  const [birthdayError, setBirthdayError] = useState(null);

  /**
   * Récupère le statut du service d'anniversaires
   */
  const fetchBirthdayStatus = useCallback(async () => {
    setBirthdayLoading(true);
    setBirthdayError(null);

    try {
      const response = await getBirthdayStatus();
      if (response.success) {
        setBirthdayStatus(response.data);
      } else {
        setBirthdayError(response.message || 'Erreur lors de la récupération du statut');
      }
    } catch (err) {
      setBirthdayError(err.message || 'Erreur lors de la récupération du statut');
    } finally {
      setBirthdayLoading(false);
    }
  }, []);

  /**
   * Récupère l'historique des annonces
   */
  const fetchBirthdayHistory = useCallback(async (limit = 30) => {
    setBirthdayLoading(true);
    setBirthdayError(null);

    try {
      const response = await getBirthdayHistory(limit);
      if (response.success) {
        setBirthdayHistory(response.data || []);
      } else {
        setBirthdayError(response.message || 'Erreur lors de la récupération de l\'historique');
      }
    } catch (err) {
      setBirthdayError(err.message || 'Erreur lors de la récupération de l\'historique');
    } finally {
      setBirthdayLoading(false);
    }
  }, []);

  /**
   * Force l'envoi d'une annonce d'anniversaire (mode test)
   */
  const handleTestAnnounce = useCallback(async () => {
    setBirthdayLoading(true);
    setBirthdayResult(null);
    setBirthdayError(null);

    try {
      const response = await testBirthdayAnnounce();
      setBirthdayResult(response);
      // Rafraîchir le statut après le test
      await fetchBirthdayStatus();
    } catch (err) {
      setBirthdayError(err.message || 'Erreur lors du test d\'annonce');
    } finally {
      setBirthdayLoading(false);
    }
  }, [fetchBirthdayStatus]);

  /**
   * Vérifie les anniversaires et envoie l'annonce si nécessaire
   */
  const handleCheckBirthdays = useCallback(async () => {
    setBirthdayLoading(true);
    setBirthdayResult(null);
    setBirthdayError(null);

    try {
      const response = await checkBirthdays();
      setBirthdayResult(response);
      // Rafraîchir le statut et l'historique après la vérification
      await fetchBirthdayStatus();
      await fetchBirthdayHistory();
    } catch (err) {
      setBirthdayError(err.message || 'Erreur lors de la vérification des anniversaires');
    } finally {
      setBirthdayLoading(false);
    }
  }, [fetchBirthdayStatus, fetchBirthdayHistory]);

  /**
   * Rafraîchit toutes les données
   */
  const refreshAll = useCallback(async () => {
    await fetchBirthdayStatus();
    await fetchBirthdayHistory();
  }, [fetchBirthdayStatus, fetchBirthdayHistory]);

  return {
    birthdayStatus,
    birthdayHistory,
    birthdayLoading,
    birthdayResult,
    birthdayError,
    fetchBirthdayStatus,
    fetchBirthdayHistory,
    handleTestAnnounce,
    handleCheckBirthdays,
    refreshAll,
  };
};
